import React, { useRef, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { ChevronDown, AlertCircle } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  /**
   * Texto secundário renderizado abaixo do label na lista de opções (não
   * aparece no input após selecionar). Use para informação complementar —
   * ex.: e-mail sob o nome do usuário, CNPJ sob a razão social. Opcional:
   * opções sem `description` mantêm o layout de linha única.
   */
  description?: string;
}

interface AutocompleteSelectProps {
  label: string;
  icon?: React.ReactNode;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
}

export const AutocompleteSelect: React.FC<AutocompleteSelectProps> = ({
  label,
  icon,
  options,
  value,
  onChange,
  error,
  disabled,
}) => {
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const generatedId = React.useId();
  const errorId = `${generatedId}-error`;

  const selectedOption = options.find((opt) => opt.value === value) ?? null;
  const isFloated = isFocused || !!selectedOption;

  const filteredOptions =
    query === ''
      ? options
      : options.filter((opt) =>
          opt.label.toLowerCase().includes(query.toLowerCase())
        );

  const borderColor = error
    ? 'border-red-500'
    : isFocused
    ? 'border-blue-500'
    : 'border-gray-300';

  return (
    <div className="w-full">
      <Combobox
        value={selectedOption}
        onChange={(opt: SelectOption | null) => onChange(opt?.value ?? '')}
        disabled={disabled}
      >
        {({ open: isOpen }: { open: boolean }) => (
          <div className={`relative h-14 ${disabled ? 'opacity-60' : ''}`}>
            {icon && (
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10">
                {icon}
              </div>
            )}

            <Combobox.Input
              aria-label={label}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
              displayValue={(opt: SelectOption | null) => opt?.label ?? ''}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => { setIsFocused(false); setQuery(''); }}
              onClick={() => { if (!isOpen) buttonRef.current?.click(); }}
              className={[
                'w-full h-full rounded-lg border bg-white',
                'focus:outline-none focus:ring-2 focus:ring-blue-500',
                icon ? 'pl-10' : 'pl-4',
                'pr-10',
                borderColor,
                disabled ? 'bg-gray-50 cursor-not-allowed' : 'cursor-pointer',
              ].join(' ')}
            />

            <label
              className={[
                'absolute pointer-events-none transition-all duration-200 select-none z-10',
                isFloated
                  ? `left-3 -top-2.5 text-xs bg-white px-1 font-medium ${
                      error ? 'text-red-500' : isFocused ? 'text-blue-600' : 'text-gray-500'
                    }`
                  : `${icon ? 'left-10' : 'left-4'} top-1/2 -translate-y-1/2 text-sm text-gray-400`,
              ].join(' ')}
            >
              {label}
            </label>

            <Combobox.Button
              ref={buttonRef as React.Ref<HTMLButtonElement>}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
            >
              <ChevronDown className="w-4 h-4" />
            </Combobox.Button>

            <Combobox.Options className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto top-full">
              {filteredOptions.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-500">Nenhuma opção encontrada</div>
              ) : (
                filteredOptions.map((opt) => (
                  <Combobox.Option
                    key={opt.value}
                    value={opt}
                    className={({ active }) =>
                      `px-4 py-2 text-sm cursor-pointer select-none ${
                        active ? 'bg-blue-50' : ''
                      }`
                    }
                  >
                    {({ active }) => (
                      <div className="min-w-0">
                        <div className={`font-medium truncate ${active ? 'text-blue-700' : 'text-gray-900'}`}>
                          {opt.label}
                        </div>
                        {opt.description && (
                          <div className={`text-xs truncate ${active ? 'text-blue-600/80' : 'text-gray-500'}`}>
                            {opt.description}
                          </div>
                        )}
                      </div>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </div>
        )}
      </Combobox>

      {error && (
        <p
          id={errorId}
          role="alert"
          className="mt-1 text-sm text-red-600 flex items-center gap-1"
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
};
